import type { ReactNode } from "react";
import { cn, layout, spacing, typography } from "@/utilities/tailwind-utils";

/**
 * Page Header props
 */
interface PageHeaderProps {
  title: string;
  secondaryLabel?: string;
  className?: string;
  rightSlot?: ReactNode;
  imageSrc?: string;
  imageAlt?: string;
  imageNode?: ReactNode;
}

/**
 * Page Header component
 * @param props - PageHeaderProps
 * @returns PageHeader component
 */
export function PageHeader({
  title,
  secondaryLabel,
  className,
  rightSlot,
  imageSrc,
  imageAlt,
  imageNode
}: PageHeaderProps) {
  return (
    <div
      className={cn(
        layout.flexBetween,
        spacing.mb1,
        spacing.gap2,
        "common-page-header",
        className
      )}
    >
      {/* Left: Image + Titles */}
      <div
        className={cn(layout.flexCenterGap4, layout.flex1, "gap-2 sm:gap-4")}
      >
        {/* Image */}
        <div
          className={cn(
            layout.flexCenter,
            "size-8 sm:size-12 rounded-lg overflow-hidden bg-primary/10"
          )}
        >
          {imageNode ? (
            imageNode
          ) : imageSrc ? (
            <img
              src={imageSrc}
              alt={imageAlt || title}
              className="size-8 sm:size-12 object-cover"
            />
          ) : (
            <img
              src="/assets/images/platform-profile-icon.svg"
              alt="Page"
              className="size-8 sm:size-12 object-cover"
            />
          )}
        </div>

        {/* Titles */}
        <div className={cn(layout.flexColGapHalf, layout.flex1)}>
          <h1
            className={cn(
              typography.text2xlBold,
              typography.textPrimary,
              "text-lg sm:text-2xl"
            )}
          >
            {title}
          </h1>
          {secondaryLabel && (
            <p
              className={cn(
                typography.textSmMuted,
                "common-page-header-secondary-label text-xs sm:text-sm"
              )}
            >
              {secondaryLabel}
            </p>
          )}
        </div>
      </div>

      {/* Right: Custom slot */}
      {rightSlot && (
        <div className={cn(layout.flexCenterGap3)}>{rightSlot}</div>
      )}
    </div>
  );
}

export default PageHeader;
